import DownloadCSVMenuButton, {
    downloadTypes,
} from 'components/Button/DownloadCSVMenuButton';
import { GOOGLE_METADATA_BY_KEY, GOOGLE_SORT_OPTIONS } from 'utils/metrics';
import { MdOutlineShowChart, MdOutlineStackedLineChart } from 'react-icons/md';
import {
    TIKTOK_COUNTRIES,
    brandHealthStats,
    brandHealthStatsNonTikTok,
    categoryHealthStats,
    categoryHealthStatsNonTikTok,
} from '@/config/constants';
import {
    buildExportFilename,
    downloadItemsAsCSV,
    downloadTimeSeriesAsCSV,
} from 'utils/formatCSV';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import AddBySearch from 'components/Navigation/Search/AddBySearch';
import { BannerRow as BannerRowContent } from './BannerRow';
import DataTable from 'components/DataVis/DataTable/DataTable';
import FilterSelect from 'components/Input/FilterSelect';
import IconButton from 'components/Button/IconButton';
import NameCellWithTooltip from '../NameCellWithTooltip';
import Stack from 'components/Stack';
import WatchlistTimeSeries from 'components/DataVis/WatchlistTimeSeries';
import { createDefaultColumns } from 'components/DataVis/DataTable/utils';
import downloadImage from 'utils/downloadImage';
import { getOnChange } from 'components/SectionTableFilterChips';
import { getSeriesColors } from 'components/DataVis/Charts/MultiPredictionTimeSeriesChart';
import { healthStatFilterOptions } from 'utils/filters';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';
import { useCardsFilterSort } from 'components/CardsWrapper';
import { useSearchWatchlistData } from './useSearchWatchlistData';
import useTableColumns from 'utils/hooks/useTableColumns';
import useEndDate from 'utils/hooks/useEndDate';

function getMetrics({ isTikTokMarket } = {}) {
    const metrics = [
        [GOOGLE_METADATA_BY_KEY.volume, true],
        [GOOGLE_METADATA_BY_KEY.absoluteGrowth, true],
        [GOOGLE_METADATA_BY_KEY.YoY, true],
        [GOOGLE_METADATA_BY_KEY.QoQ_increase, false],
        [GOOGLE_METADATA_BY_KEY.QoQ, false],
        [GOOGLE_METADATA_BY_KEY.MoM_increase, false],
        [GOOGLE_METADATA_BY_KEY.MoM, false],
        // Prediction is hidden in markets that support the pop index, matching
        // the single-entity Google pages (see Brand/Google.jsx).
        ...(isTikTokMarket
            ? []
            : [[GOOGLE_METADATA_BY_KEY.predictedYoY, true]]),
    ];
    return metrics;
}

function getHealthStatKeys({ isNotTikTokMarket, brandIds, categoryIds }) {
    if (brandIds && categoryIds) {
        return isNotTikTokMarket
            ? brandHealthStats.concat(categoryHealthStats)
            : brandHealthStatsNonTikTok.concat(categoryHealthStatsNonTikTok);
    }
    if (brandIds) {
        return isNotTikTokMarket ? brandHealthStats : brandHealthStatsNonTikTok;
    }
    return isNotTikTokMarket
        ? categoryHealthStats
        : categoryHealthStatsNonTikTok;
}

function getHealthStatFilterOptions({
    isNotTikTokMarket,
    brandIds,
    categoryIds,
}) {
    const healthStatKeys = getHealthStatKeys({
        isNotTikTokMarket,
        brandIds,
        categoryIds,
    });
    return healthStatFilterOptions.filter(({ label }) =>
        healthStatKeys.includes(label),
    );
}

const TableTools = styled(Stack).attrs(
    ({ $direction = 'row', $space = '.5em' }) => ({
        $direction,
        $space,
    }),
)`
    display: flex;
    justify-content: flex-end;
`;

const INITIAL_NUM_LINES = 12;

const SearchWatchlist = memo(
    ({
        brandIds,
        categoryIds,
        affinityCategoryIds,
        affinityBrandIds,
        countryTextId,
        country,
        countryId,
        mixpanelMeta,
        onRemoveItem,
        onRowClick,
        renderHits,
        watchlistName,
        watchlistId,
    }) => {
        const isTikTokMarket =
            !!countryTextId && TIKTOK_COUNTRIES.includes(countryTextId);
        const isNotTikTokMarket = !!countryTextId && !isTikTokMarket;
        const googleMetrics = getMetrics({ isTikTokMarket });
        const googleSortOptions = Object.values(GOOGLE_SORT_OPTIONS).filter(
            ({ sortBy }) => googleMetrics.some(([{ key }]) => key === sortBy),
        );
        const defaultInitialSortOption = googleSortOptions.find(
            ({ sortBy }) => sortBy === GOOGLE_METADATA_BY_KEY.volume.key,
        );
        const date = useEndDate('google');

        const {
            brandsQuery,
            categoriesQuery,
            categoryAffinitiesQuery,
            brandAffinitiesQuery,
            allSearchData,
        } = useSearchWatchlistData({
            brandIds,
            categoryIds,
            affinityCategoryIds,
            affinityBrandIds,
            countryId,
            country,
        });

        const loadingData = !!(
            categoriesQuery.isLoading ||
            brandsQuery.isLoading ||
            categoryAffinitiesQuery.isLoading ||
            brandAffinitiesQuery.isLoading ||
            (categoryIds?.length && categoriesQuery.isPlaceholderData) ||
            (brandIds?.length && brandsQuery.isPlaceholderData) ||
            (affinityCategoryIds?.length &&
                categoryAffinitiesQuery.isLoading) ||
            (affinityBrandIds?.length && brandAffinitiesQuery.isLoading)
        );

        const dataReady =
            allSearchData && allSearchData.length > 0 && !loadingData;
        const {
            filters,
            filtersKey,
            maxSortMetric,
            onSearchInputChange,
            setFilter,
            setFilters,
            resetFilters,
            setSortOption: baseSetSortOption,
            initialSortOption,
            sortOption,
            sortOptionKey,
            sortedData,
        } = useCardsFilterSort({
            rootCategoryId: watchlistId,
            data: allSearchData,
            sortOptions: googleSortOptions,
            initialSortOption: defaultInitialSortOption,
            filterMissingData: false,
        });
        const TABLE_NAME = 'watchlist-search';
        const { visibleOrderedMetrics, columnsMenu, setSortOption } =
            useTableColumns({
                tableName: TABLE_NAME,
                metrics: googleMetrics,
                setSortOption: baseSetSortOption,
            });

        const visibleColumnsConfig = createDefaultColumns(
            ['name', ...visibleOrderedMetrics],
            { showHealthStatLabels: true },
        );

        visibleColumnsConfig[0].formatCell = ({ row, value }) => (
            <NameCellWithTooltip row={row} value={value} />
        );

        const setWatchlistSortOption = useCallback(
            (sortOption) => {
                setSortOption(
                    googleSortOptions.find(
                        ({ sortBy, ascending }) =>
                            sortBy === sortOption.sortBy &&
                            ascending === sortOption.ascending,
                    ),
                );
            },
            [setSortOption],
        );

        const timeSeriesRef = useRef();

        const timeSeriesData = sortedData
            ? sortedData.filter(
                  ({ timeSeries }) => timeSeries && timeSeries.length,
              )
            : null;
        const noDataMessage = !allSearchData
            ? ''
            : sortedData.length && !timeSeriesData.length
              ? `Not enough data to render time series chart.`
              : `No trends available.${Object.keys(filters).length ? ' Try adjusting the filters.' : ''}`;

        const [visibleIds, setVisibleIds] = useState(null);
        const [colorsById, setColorsById] = useState(null);
        const [removedColor, setRemovedColor] = useState(null);
        const [viewSumTimeSeries, setViewSumTimeSeries] = useState(false);

        useEffect(() => {
            setVisibleIds(null);
            setColorsById(null);
            setRemovedColor(null);
        }, [filters]);

        let data;
        let visibleTimeSeriesData;
        let timeSeriesDataIds;

        if (sortedData) {
            data = [];
            visibleTimeSeriesData = [];
            timeSeriesDataIds = [];
            for (let i = 0; i < sortedData.length; i++) {
                const row = sortedData[i];
                const hasTimeSeriesData =
                    row.timeSeries && row.timeSeries.length;
                const isInitialId =
                    !visibleIds &&
                    visibleTimeSeriesData.length < INITIAL_NUM_LINES &&
                    hasTimeSeriesData;
                const isVisibleId = visibleIds && visibleIds.includes(row.id);
                const checked = !!(isInitialId || isVisibleId);
                const color =
                    hasTimeSeriesData && checked
                        ? (colorsById && colorsById[row.id]) ||
                          getSeriesColors(visibleTimeSeriesData.length)
                        : undefined;
                const formattedRow = { ...row, checked, color };
                data.push(formattedRow);
                if (checked && hasTimeSeriesData)
                    visibleTimeSeriesData.push(formattedRow);
                if (hasTimeSeriesData) timeSeriesDataIds.push(row.id);
            }
        }

        const onToggleCheck = ({ node, checked }) => {
            const nextColorsById = colorsById
                ? { ...colorsById }
                : data.reduce((acc, { id, color }, idx) => {
                      if (color) acc[id] = color;
                      return acc;
                  }, {});

            if (!node) {
                const currentVisible = (
                    !visibleIds
                        ? data.filter(({ checked }) => checked)
                        : visibleIds
                ).length;
                setVisibleIds(
                    !checked && currentVisible < timeSeriesDataIds.length
                        ? timeSeriesDataIds
                        : !checked
                          ? []
                          : null,
                );
                setColorsById(null);
            } else if (checked && visibleIds && !visibleIds.includes(node.id)) {
                setVisibleIds(visibleIds.concat([node.id]));
                nextColorsById[node.id] =
                    removedColor || getSeriesColors(visibleIds.length);
                setColorsById(nextColorsById);
                if (removedColor) setRemovedColor(null);
            } else if (!checked && visibleIds && visibleIds.includes(node.id)) {
                setVisibleIds(visibleIds.filter((id) => id !== node.id));
                setRemovedColor(nextColorsById[node.id]);
                delete nextColorsById[node.id];
                setColorsById(nextColorsById);
            } else if (checked) {
                setVisibleIds(
                    visibleTimeSeriesData.map(({ id }) => id).concat([node.id]),
                );
                nextColorsById[node.id] =
                    removedColor ||
                    getSeriesColors(visibleTimeSeriesData.length);
                setColorsById(nextColorsById);
                if (removedColor) setRemovedColor(null);
            } else {
                setVisibleIds(
                    visibleTimeSeriesData
                        .filter(({ id }) => id !== node.id)
                        .map(({ id }) => id),
                );
                setRemovedColor(nextColorsById[node.id]);
                delete nextColorsById[node.id];
                setColorsById(nextColorsById);
            }
        };

        const unfilteredCount = allSearchData?.filter(
            ({ time_series }) => time_series && time_series.length,
        ).length;
        const visibleCount = visibleTimeSeriesData
            ? visibleTimeSeriesData.length
            : 0;
        const rowHeight = sortedData?.find((s) => s.type === 'affinity')
            ? 60
            : 50;

        return (
            <>
                <div
                    css={`
                        padding: 1.25rem;
                        padding-top: 0;
                        margin: 0 -1.25rem 1.25rem;
                        border-bottom: 1px solid ${({ theme }) => theme.gray_5};
                        display: flex;
                        justify-content: space-between;
                    `}
                >
                    <IconButton
                        size="sm"
                        IconLeft={
                            viewSumTimeSeries
                                ? MdOutlineStackedLineChart
                                : MdOutlineShowChart
                        }
                        onClick={(e) =>
                            setViewSumTimeSeries(!viewSumTimeSeries)
                        }
                        mixpanelContext={
                            viewSumTimeSeries
                                ? 'View Separated Time Series'
                                : 'View Combined Time Series'
                        }
                        isDisabled={!visibleCount}
                    >
                        {viewSumTimeSeries ? 'View Separated' : 'View Combined'}
                    </IconButton>
                    <DownloadCSVMenuButton
                        isDisabled={!dataReady || !date}
                        isLoading={loadingData || !date}
                        options={[
                            {
                                mixpanelMeta: { label: 'card data csv' },
                                children: 'Table Data CSV',
                                onClick: () =>
                                    downloadItemsAsCSV(
                                        buildExportFilename({
                                            entity: watchlistName,
                                            country,
                                            source: 'google',
                                            type: 'trend',
                                            kind: 'data',
                                            date,
                                        }),
                                        sortedData,
                                        [
                                            {
                                                key: 'name',
                                                accessor: ({
                                                    exportName,
                                                    name,
                                                }) => exportName || name || '',
                                            },
                                            ...visibleOrderedMetrics.map(
                                                ({
                                                    key,
                                                    tableHeadColumnLabel: label,
                                                }) => ({ key, label }),
                                            ),
                                        ],
                                    ),
                                downloadType: downloadTypes.CSV,
                                key: 'card data csv',
                            },
                            {
                                mixpanelMeta: { label: 'time series csv' },
                                children: 'Time Series CSV',
                                onClick: () =>
                                    downloadTimeSeriesAsCSV(
                                        buildExportFilename({
                                            entity: watchlistName,
                                            country,
                                            source: 'google',
                                            type: 'trend',
                                            kind: 'time-series',
                                            date,
                                        }),
                                        sortedData,
                                        { skipPrediction: isTikTokMarket },
                                    ),
                                downloadType: downloadTypes.CSV,
                                key: 'time series csv',
                            },
                            {
                                mixpanelMeta: { label: 'time series image' },
                                children: 'Time Series Image',
                                onClick: () =>
                                    downloadImage(timeSeriesRef.current, {
                                        title: `${watchlistName} (${country ? country.code || country.name : null})`,
                                        filename: buildExportFilename({
                                            date,
                                            country,
                                            source: 'google',
                                            entity: watchlistName,
                                            type: 'trend',
                                            kind: 'time-series',
                                        }),
                                    }),
                                downloadType: downloadTypes.IMAGE,
                                key: 'time series image',
                            },
                        ]}
                        mixpanelMeta={mixpanelMeta}
                    />
                </div>
                <WatchlistTimeSeries
                    ref={timeSeriesRef}
                    noDataMessage={noDataMessage}
                    data={visibleTimeSeriesData}
                    isLoading={loadingData}
                    viewSumTimeSeries={viewSumTimeSeries}
                    skipPrediction={isTikTokMarket}
                />
                <div
                    css={`
                        padding: ${({ theme }) => theme.space_8}px 0;
                        font-weight: bold;
                        font-size: 0.75em;
                    `}
                >
                    {`Showing ${loadingData ? '... of ...' : `${visibleCount} of ${unfilteredCount}`} items. Use the list to add and remove items.`}
                </div>
                <Stack $space={(theme) => `${theme.space_10}px;`}>
                    <TableTools
                        css={`
                            display: flex;
                            justify-content: space-between;
                        `}
                    >
                        <FilterSelect
                            filters={filters}
                            setFilter={setFilter}
                            setFilters={setFilters}
                            resetFilters={resetFilters}
                            sortOption={sortOption}
                            setSortOption={setWatchlistSortOption}
                            initialSortOption={initialSortOption}
                            mixpanelMeta={mixpanelMeta}
                            menus={['cluster', 'healthStats']}
                            getOnChange={getOnChange}
                            healthStatFilterOptions={getHealthStatFilterOptions(
                                { isNotTikTokMarket, brandIds, categoryIds },
                            )}
                            showAllHealthStats={true}
                            menuAlign="left"
                        />
                        <div
                            css={`
                                display: flex;
                                gap: 12px;
                            `}
                        >
                            {columnsMenu}
                            {renderHits ? (
                                <AddBySearch
                                    countryTextId={countryTextId}
                                    renderHits={renderHits}
                                    panelAlign="right"
                                    dataSource="google"
                                />
                            ) : null}
                        </div>
                    </TableTools>
                    <DataTable
                        dataKey={`d-*-s-${sortOptionKey}-f-${filtersKey}`}
                        maxSortMetric={maxSortMetric}
                        onSearchInputChange={onSearchInputChange}
                        sortOption={sortOption}
                        setSortOption={setWatchlistSortOption}
                        initialSortOption={initialSortOption}
                        mixpanelMeta={mixpanelMeta}
                        noDataMessage={noDataMessage}
                        data={data}
                        name={watchlistName || TABLE_NAME}
                        onRowClick={onRowClick}
                        onRemoveItem={onRemoveItem}
                        onToggleCheck={onToggleCheck}
                        columns={visibleColumnsConfig}
                        rowHeight={rowHeight}
                        BannerRowContent={
                            <BannerRowContent
                                watchlistId={watchlistId}
                                mixpanelMeta={mixpanelMeta}
                            />
                        }
                    />
                </Stack>
            </>
        );
    },
    isEqual,
);

export default SearchWatchlist;
